"use client"

import {Separator} from "@/components/ui/separator";
import {SidebarTrigger} from "@/components/ui/sidebar";
import {useLocation} from "react-router-dom";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Button} from "@/components/ui/button";
import {Trash, Clock, Calendar, Trash2, Check, X} from 'lucide-react';
import {Status, StatusIndicator} from '@/components/ui/shadcn-io/status';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from "react";
import axiosInstance from "@/api/axios";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import CustomCalendar from "@/common/Components/Calendar/Calendar";

interface BinObject {
    id: string;
    is_garby_vision?: boolean;
    status?: 'online' | 'offline' | 'maintenance' | 'degraded';
    last_update?: string;
    last_seen?: string;
    [key: string]: any;
}

type BinType = string | BinObject;

interface SiteHeaderProps {
    timeRangeDropdownOpen?: boolean;
    setTimeRangeDropdownOpen?: (open: boolean) => void;
    binDropdownOpen?: boolean;
    setBinDropdownOpen?: (open: boolean) => void;
    selectedTimeRange?: string;
    selectTimeRange?: (timeRange: string) => boolean;
    timeRangeOptions?: string[];
    selectedBins?: string[];
    allBins?: BinObject[];
    toggleBinSelection?: (bin: string) => boolean;
    yearDropdownOpen?: boolean;
    setYearDropdownOpen?: (open: boolean) => void;
    selectedYear?: string;
    selectYear?: (year: string) => boolean;
    yearOptions?: string[];
    wasteTypeDropdownOpen?: boolean;
    setWasteTypeDropdownOpen?: (open: boolean) => void;
    selectedWasteTypes?: string[];
    toggleWasteTypeSelection?: (wasteType: string) => boolean;
    wasteTypeOptions?: string[];
    customDateRange?: [{
        startDate: Date | null;
        endDate: Date | null;
        key: string;
    }];
    onCustomDateRangeChange?: (dateRange: any, isCustom: boolean) => void;
    isCustomDateRange?: boolean;
}

export function SiteHeader({
                               timeRangeDropdownOpen,
                               setTimeRangeDropdownOpen,
                               binDropdownOpen,
                               setBinDropdownOpen,
                               selectedTimeRange,
                               selectTimeRange,
                               timeRangeOptions,
                               selectedBins,
                               allBins,
                               toggleBinSelection,
                               yearDropdownOpen,
                               setYearDropdownOpen,
                               selectedYear,
                               selectYear,
                               yearOptions,
                               wasteTypeDropdownOpen,
                               setWasteTypeDropdownOpen,
                               selectedWasteTypes,
                               toggleWasteTypeSelection,
                               wasteTypeOptions,
                               customDateRange,
                               onCustomDateRangeChange,
                               isCustomDateRange,
                           }: SiteHeaderProps) {
    const location = useLocation();
    const {t} = useTranslation();
    const [binsWithStatus, setBinsWithStatus] = useState<BinObject[]>([]);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [localDateRange, setLocalDateRange] = useState(customDateRange || [{
        startDate: null,
        endDate: null,
        key: 'selection'
    }]);
    const [isLocalCustomDateRange, setIsLocalCustomDateRange] = useState(isCustomDateRange || false);


    const formatLastSeen = (iso: string): string => {
        const d = new Date(iso);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${mm}${dd}-${hh}:${min}`;
    };
    useEffect(() => {
        if (customDateRange) {
            setLocalDateRange(customDateRange);
        }
        if (isCustomDateRange !== undefined) {
            setIsLocalCustomDateRange(isCustomDateRange);
        }
    }, [customDateRange, isCustomDateRange]);

    useEffect(() => {
        const fetchBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];
                const validBins = binsWithStatus.filter((bin: any): bin is BinObject =>
                    bin && typeof bin === 'object' && bin.id && typeof bin.id === 'string'
                );
                setBinsWithStatus(validBins);
            } catch (error) {
                console.error("Error fetching bin statuses:", error);
                setBinsWithStatus([]);
            }
        };
        fetchBinStatuses();
        const interval = setInterval(fetchBinStatuses, 600000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const fetchBinStatusesForDropdown = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];
                const validBins = binsWithStatus.filter((bin: any): bin is BinObject =>
                    bin && typeof bin === 'object' && bin.id && typeof bin.id === 'string'
                );
                setBinsWithStatus(validBins);
            } catch (error) {
                console.error("Error fetching bin statuses:", error);
                setBinsWithStatus([]);
            }
        };
        if (binDropdownOpen) {
            fetchBinStatusesForDropdown();
        }
    }, [binDropdownOpen]);

    const binsToDisplay = binsWithStatus.length > 0 ? binsWithStatus : (allBins || []);

    const getHeaderTitle = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts.length === 0) return t('navigation.dashboard');
        if (location.pathname.includes('/account/security')) return '';
        const lastPart = pathParts[pathParts.length - 1];
        const routeTitles: Record<string, string> = {
            'profile': t('navigation.profile'),
            'circularity': t('navigation.circularity'),
            'recycling': t('navigation.recyclingDashboard'),
            'documents': t('navigation.documents'),
            'login': t('navigation.login'),
            'signup': t('navigation.signup'),
            'thanks': t('navigation.thankYou'),
            'logout': t('navigation.logout'),
            'cost-savings': t('navigation.costSavings'),
            'homepage': t('navigation.homePage'),
            'operations': t('navigation.operations'),
        };
        return routeTitles[lastPart] || lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
    };

    const getTimeRangeTranslation = (timeRange: string) => {
        const translations: Record<string, string> = {
            'Day': t('timeRanges.Day'),
            'Week': t('timeRanges.Week'),
            'Month': t('timeRanges.Month'),
            '3Month': t('timeRanges.3Month'),
            '3 Months': t('timeRanges.3Month'),
            '6Month': t('timeRanges.6Month'),
            '6 Months': t('timeRanges.6Month'),
            'Year': t('timeRanges.Year'),
            'Custom': t('timeRanges.Custom') || 'Custom'
        };
        return translations[timeRange] || timeRange;
    };

    const isDashboardRoute = () => {
        return location.pathname === "/" ||
            location.pathname === "/operations" ||
            location.pathname.includes("dashboard");
    };

    const selectAllBins = () => {
        if (binsToDisplay && toggleBinSelection) {
            binsToDisplay.forEach(bin => {
                const binId = typeof bin === 'object' && bin !== null ? bin.id : bin;
                if (!selectedBins?.includes(binId)) {
                    toggleBinSelection(binId);
                }
            });
        }
    };

    const clearAllBins = () => {
        if (selectedBins && toggleBinSelection) {
            selectedBins.forEach(bin => { toggleBinSelection(bin); });
        }
    };

    const handleDateChange = (newDateValue: any) => {
        console.log('SiteHeader: Date changed in calendar:', newDateValue);
        setLocalDateRange(newDateValue);
        if (newDateValue && newDateValue[0] && newDateValue[0].startDate && newDateValue[0].endDate) {
            setIsLocalCustomDateRange(true);
            setCalendarOpen(false);
            setTimeRangeDropdownOpen?.(false);
            if (onCustomDateRangeChange) {
                console.log('SiteHeader: Calling onCustomDateRangeChange with custom dates');
                onCustomDateRangeChange(newDateValue, true);
            }
        }
    };

    const handleResetToTimeRange = () => {
        console.log('SiteHeader: Resetting to time range');
        const resetValue = [{startDate: null, endDate: null, key: 'selection'}];
        setLocalDateRange(resetValue);
        setIsLocalCustomDateRange(false);
        if (onCustomDateRangeChange) {
            onCustomDateRangeChange(resetValue, false);
        }
        if (selectTimeRange && timeRangeOptions && timeRangeOptions.length > 0) {
            selectTimeRange(timeRangeOptions[0]);
        }
    };

    const getDisplayText = () => {
        if (isLocalCustomDateRange && localDateRange[0]?.startDate && localDateRange[0]?.endDate) {
            const startDate = localDateRange[0].startDate instanceof Date
                ? localDateRange[0].startDate
                : new Date(localDateRange[0].startDate);
            const endDate = localDateRange[0].endDate instanceof Date
                ? localDateRange[0].endDate
                : new Date(localDateRange[0].endDate);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
            }
        }
        return selectedTimeRange ? getTimeRangeTranslation(selectedTimeRange) : (selectedTimeRange || 'Select Time');
    };

    return (
        <header
            className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
            <div className="flex w-full items-center justify-between gap-1">
                <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
                    <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9"/>
                    <Separator
                        orientation="vertical"
                        className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4"
                    />
                    <h1 className="text-sm sm:text-base font-medium truncate">{getHeaderTitle()}</h1>
                </div>

                {isDashboardRoute() && (
                    <div className="flex gap-2">

                        {/* Bins dropdown */}
                        {selectedBins && toggleBinSelection && (
                            <DropdownMenu modal={false} open={binDropdownOpen} onOpenChange={setBinDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Trash className="h-4 w-4 sm:mr-2 opacity-50"/>
                                        <span className="hidden sm:inline">{t('header.filters.bins')}: {selectedBins.length}</span>
                                        <span className="sm:hidden">{selectedBins.length}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56">
                                    <div className="flex items-center justify-between px-2 py-1.5">
                                        <DropdownMenuLabel className="py-0">{t('header.filters.selected')}</DropdownMenuLabel>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={(e) => { e.preventDefault(); selectAllBins(); }}
                                            >
                                                <Check className="h-3 w-3 mr-1"/>
                                                {t('common.all')}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={(e) => { e.preventDefault(); clearAllBins(); }}
                                            >
                                                <X className="h-3 w-3 mr-1"/>
                                                {t('common.clear')}
                                            </Button>
                                        </div>
                                    </div>
                                    <DropdownMenuSeparator/>
                                    {binsToDisplay && [...binsToDisplay]
                                        .filter(bin => bin != null)
                                        .sort((a, b) => {
                                            const binA = typeof a === 'object' && a ? a.id : a;
                                            const binB = typeof b === 'object' && b ? b.id : b;
                                            if (!binA || !binB) return 0;
                                            const binAStr = String(binA);
                                            const binBStr = String(binB);
                                            const nameA = binAStr.replace(/\d+/g, '').toLowerCase();
                                            const nameB = binBStr.replace(/\d+/g, '').toLowerCase();
                                            if (nameA < nameB) return -1;
                                            if (nameA > nameB) return 1;
                                            const numA = parseInt(binAStr.match(/\d+/)?.[0] || '0');
                                            const numB = parseInt(binBStr.match(/\d+/)?.[0] || '0');
                                            return numA - numB;
                                        })
                                        .map(bin => {
                                            const binId = typeof bin === 'object' && bin ? bin.id : bin;
                                            const binStatus = typeof bin === 'object' && bin ? bin.status || 'offline' : 'offline';
                                            if (!binId) return null;
                                            return (
                                       <DropdownMenuCheckboxItem
    key={binId}
    checked={selectedBins.includes(binId)}
    onSelect={(e) => { e.preventDefault(); toggleBinSelection(binId); }}
    // Keep pl-2/pr-2 to use the full width, and hide default span
    className="flex items-center justify-center py-2 pl-2 pr-2 [&>span]:hidden"
>
    <div className="flex flex-col items-center w-full">
        {/* This container is the key. 
           We use a grid with 3 fixed-width columns: 
           [Checkmark] [ID Text] [Status Indicator]
        */}
        <div className="grid grid-cols-[24px_1fr_32px] items-center gap-2 w-fit min-w-[120px]">
            
            {/* 1. Aligned Checkmark Column */}
            <div className="flex justify-center">
                {selectedBins.includes(binId) && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                )}
            </div>
            
            {/* 2. Bin ID Column - Centered text */}
            <span className="text-center font-semibold text-sm truncate">
                {binId}
            </span>
            
            {/* 3. Status Column */}
            <div className="flex justify-center">
                <Status status={binStatus}>
                    <StatusIndicator />
                </Status>
            </div>
        </div>
        
        {/* "Since" text remains centered below the grid row */}
        {binStatus === 'offline' && bin.last_seen && (
            <span className="text-[12px] text-muted-foreground whitespace-nowrap leading-tight mt-1 opacity-80">
                Since {formatLastSeen(bin.last_seen)}
            </span>
        )}
    </div>
</DropdownMenuCheckboxItem>
                                            );
                                        })
                                        .filter(item => item !== null)
                                    }
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Time Range + Custom Date dropdown */}
                        {timeRangeOptions && (
                            <>
                                <DropdownMenu modal={false} open={timeRangeDropdownOpen} onOpenChange={setTimeRangeDropdownOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm">
                                            <Clock className="h-4 w-4 sm:mr-2 opacity-50"/>
                                            <span className="hidden sm:inline truncate max-w-[200px]">
                                                {t('header.filters.period')}: {getDisplayText()}
                                            </span>
                                            <span className="sm:hidden truncate max-w-[100px]">
                                                {getDisplayText()}
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56">
                                        <div className="flex items-center justify-between px-2 py-1.5">
                                            <DropdownMenuLabel className="py-0">
                                                {t('header.filters.selectTimePeriod')}
                                            </DropdownMenuLabel>
                                            {isLocalCustomDateRange && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={(e) => { e.preventDefault(); handleResetToTimeRange(); }}
                                                >
                                                    <X className="h-3 w-3 mr-1"/>
                                                    {t('reset') || 'Reset'}
                                                </Button>
                                            )}
                                        </div>
                                        <DropdownMenuSeparator/>

                                        <DropdownMenuCheckboxItem
                                            checked={isLocalCustomDateRange}
                                            onSelect={(e) => { e.preventDefault(); setCalendarOpen(true); }}
                                        >
                                            <Calendar className="h-4 w-4 mr-2"/>
                                            {t('selectDateRange') || 'Custom Date Range'}
                                        </DropdownMenuCheckboxItem>

                                        <DropdownMenuSeparator/>

                                        {timeRangeOptions.map(timeRange => (
                                            <DropdownMenuCheckboxItem
                                                key={timeRange}
                                                checked={!isLocalCustomDateRange && timeRange === selectedTimeRange}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    handleResetToTimeRange();
                                                    if (selectTimeRange) selectTimeRange(timeRange);
                                                }}
                                            >
                                                {getTimeRangeTranslation(timeRange)}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <DialogContent className="sm:max-w-5xl w-full">
                                        <DialogHeader>
                                            <DialogTitle>{t('selectDateRange') || 'Select Date Range'}</DialogTitle>
                                        </DialogHeader>
                                        <CustomCalendar
                                            datevalue={localDateRange}
                                            onDateValueChange={handleDateChange}
                                        />
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}